import './style.scss'

import React, { useEffect, useState } from 'react'

import { ButtonColor } from '~/components/buttons'
import {
    AirportSelect, AirportSelectChangeValue, DatePicker,
} from '~/components/controls'
import { setNotification } from '~/components/notification'
import { getDateFormat } from '~/helpers/get-date-format'

import { Validator } from '../../../../helpers/validator/validator'
import {
    PublicEmptyLegFilters,
    PublicEmptyLegFilterUpdate,
} from '../../application/types'
import { model } from './model'

type FormRequestProps = {
    selected: PublicEmptyLegFilters
    isLoading: boolean
    onSubmit: () => void
    onChange: (update: PublicEmptyLegFilterUpdate) => void
}

export const FormRequest = ({
    onSubmit, selected, isLoading, onChange,
}: FormRequestProps) => {
    const [minDate, setMinDate] = useState<Date | undefined>()
    const [today, setToday] = useState<Date | undefined>()
    const textButton = 'Search'

    useEffect(() => {
        const now = new Date()
        setMinDate(now)
        setToday(now)
    }, [])

    const getViewDateValue = () => {
        if (selected.dates) {
            const { startDate, endDate } = selected.dates

            if (startDate.getTime() === endDate.getTime()) {
                return `${getDateFormat(startDate)}`
            }
            if (startDate.getFullYear() === endDate.getFullYear()) {
                return `${getDateFormat(startDate, true)} - ${getDateFormat(endDate)}`
            }

            return `${getDateFormat(startDate)} – ${getDateFormat(endDate)}`
        }
        return ''
    }

    const handleOnSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        const formIsValid = Validator.validateForm(selected, model)

        if (!formIsValid) {
            setNotification({
                type: 'error',
                message: 'Fill in all fields correctly',
            })
            return
        }

        onSubmit()
    }

    const handleFromChange = (field: string, value: AirportSelectChangeValue) => {
        if (field === 'from' && typeof value === 'string') {
            onChange({ field, value })
        }
        if (field === 'from_location' && typeof value !== 'string') {
            onChange({ field, value })
        }
    }

    const handleDestinationChange = (field: string, value: AirportSelectChangeValue) => {
        if (field === 'destination' && typeof value === 'string') {
            onChange({ field, value })
        }
        if (field === 'destination_location' && typeof value !== 'string') {
            onChange({ field, value })
        }
    }

    return (
        <div className="search-empty-legs-form">
            <form className="search-empty-legs-form__form" onSubmit={handleOnSubmit}>
                <div className="search-empty-legs-form__fields">
                    <AirportSelect
                        placeholder="From"
                        name="from"
                        selectedValue={selected.from}
                        onChange={handleFromChange}
                        modClass="search-empty-legs-form__control"
                    />

                    <AirportSelect
                        placeholder="To"
                        name="destination"
                        selectedValue={selected.destination}
                        onChange={handleDestinationChange}
                        modClass="search-empty-legs-form__control"
                    />

                    <DatePicker
                        placeholder="Date/Range"
                        minDate={minDate}
                        ranges={selected.dates ? [selected.dates] : undefined}
                        onChange={({ selection }) => {
                            if (!selection?.startDate || !selection.endDate) return

                            onChange({
                                field: 'dates',
                                value: {
                                    startDate: selection.startDate,
                                    endDate: selection.endDate,
                                    key: selection.key || 'selection',
                                },
                            })
                        }}
                        viewValue={getViewDateValue()}
                        modClass="search-empty-legs-form__control"
                        disabledDate={today ? (date) => date < today : undefined}
                    />
                </div>

                <ButtonColor
                    type="submit"
                    text={textButton}
                    modClass="search-empty-legs-form__submit search-empty-legs-form__control"
                    size="sm"
                    processing={isLoading}
                    disabled={isLoading}
                />
            </form>
        </div>
    )
}
